import { NeedsActionDto } from '@act/shared/data-transfer-objects';
import { mapLegacyResourceReferralToFormRow } from 'react-components/forms/cboResourceForm/mapToResourceDetails.util';
import {
  ActionFormResourceDetails,
  ActionFormValues,
} from '../components/useNeedActionFormtypes';

/**
 * Parameters for building reset values for the add-action dialog form.
 */
export interface BuildActionFormResetValuesParams {
  action: NeedsActionDto;
  overrideValues: Partial<ActionFormValues>;
  placesAutocompleteEnabled: boolean;
}

type ResourceReferral = NonNullable<NeedsActionDto['resourceReferrals']>[number];

/**
 * Creates a blank resource details row for the add-action form.
 *
 * @returns Empty resource details values.
 */
export function getDefaultActionResourceDetails(): ActionFormResourceDetails {
  return {
    id: -1,
    resourceName: '',
    contactName: '',
    phoneNumber: undefined,
    url: '',
    streetAddress: '',
    city: '',
    state: '',
    zipcode: '',
    shouldDelete: false,
  };
}

/**
 * Builds form reset values from the newest action in an action thread.
 *
 * @param params Action, overrides, and feature-flag context.
 * @returns Reset values for the add-action form.
 */
export function buildActionFormResetValues({
  action,
  overrideValues,
  placesAutocompleteEnabled,
}: BuildActionFormResetValuesParams): ActionFormValues {
  const latestAction = getLatestAction(action);
  const resourceReferrals = [...(latestAction.resourceReferrals || [])]
    .sort((a, b) => a.createdAt - b.createdAt)
    .map(referral =>
      mapActionResourceReferralToFormRow(referral, placesAutocompleteEnabled)
    );

  return {
    activityId: latestAction.activityId,
    date: `${latestAction.date}`,
    duration: latestAction.durationInMinutes?.toString() ?? '',
    description: latestAction.description,
    resourceReferred: resourceReferrals.length > 0,
    resourceDetails: resourceReferrals,
    ...overrideValues,
  };
}

/**
 * Finds the newest action in an original action plus subsequent actions.
 *
 * @param action Original action with optional subsequent actions.
 * @returns Most recently created action.
 */
function getLatestAction(action: NeedsActionDto): NeedsActionDto {
  return [action, ...(action.subsequentActions || [])].sort(
    (a, b) => b.createdAt - a.createdAt
  )[0];
}

/**
 * Maps a resource referral DTO into add-action form values.
 *
 * @param referral Resource referral from an action.
 * @param placesAutocompleteEnabled Whether Places-backed CBO fields are enabled.
 * @returns Form row values for the referral.
 */
function mapActionResourceReferralToFormRow(
  referral: ResourceReferral,
  placesAutocompleteEnabled: boolean
): ActionFormResourceDetails {
  return placesAutocompleteEnabled
    ? mapLegacyResourceReferralToFormRow(referral)
    : {
        id: referral?.id,
        resourceName: referral?.resourceName,
        contactName: referral?.contactName,
        phoneNumber: referral?.phoneNumber,
        url: referral?.url,
        streetAddress: referral?.streetAddress,
        city: referral?.city,
        state: referral?.state,
        zipcode: referral?.zipcode,
        shouldDelete: false,
      };
}
